import React, { useState, useEffect } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { ArrowSquareOutIcon, CheckCircleIcon, ClockIcon, EyeIcon } from "@phosphor-icons/react";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";

import type { RootStackParamList } from "../../../navigation/navigation.types";
import { useGetEnrolledStudentCoursesByAdminQuery } from "../../../graphql/generated/graphql";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import Pill from "../../../components/common/Pill/Pill";
import AdminTable from "../../../components/adminDashboard/AdminTable/AdminTable";
import AdminTableHeader from "../../../components/adminDashboard/AdminTable/AdminTableHeader";
import AdminTablePagination from "../../../components/adminDashboard/AdminTable/AdminTablePagination";
import usePagination from "../../../hooks/usePagination";
import useSortingDropdown from "../../../hooks/useSortingDropdown";

interface Student {
  gpa?: number | null;
  totalCredits?: number | null;
  completedCourses?: number;
  coursesInProgress?: number;
  details: {
    courses: Array<{
      id: string;
      label: string;
      completion_percentage: number;
    }>;
  };
}

interface AcademicOverviewTabProps {
  student: Student;
  integration_id: string;
  studentId: string;
}

const SORT_ITEMS = [
  { label: "Course Name (A-Z)", value: "name_asc" },
  { label: "Course Name (Z-A)", value: "name_desc" },
  { label: "Date Enrolled (Newest)", value: "date_desc" },
  { label: "Date Enrolled (Oldest)", value: "date_asc" },
];

const AcademicOverviewTab: React.FC<AcademicOverviewTabProps> = ({ student, integration_id, studentId }) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [searchQuery, setSearchQuery] = useState("");

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 5 });

  // Use the new admin query
  const { data: coursesData, refetch: coursesRefetch } = useGetEnrolledStudentCoursesByAdminQuery({
    variables: {
      schoolId: integration_id,
      studentId,
      query: searchQuery,
      page,
      pageSize,
      sortBy,
    },
    skip: !integration_id || !studentId,
  });

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    coursesRefetch();
  }, [searchQuery, page, pageSize, sortBy, coursesRefetch]);

  const getStatusColor = (progress?: number) => {
    if (progress !== undefined) {
      if (progress === 0) {
        return { backgroundColor: "#F3F4F6", textColor: "#374151" }; // Not Started
      } else if (progress > 0 && progress < 100) {
        return { backgroundColor: "#FEF3C7", textColor: "#92400E" }; // In Progress
      } else if (progress >= 100) {
        return { backgroundColor: "#D1FAE5", textColor: "#065F46" }; // Completed
      }
    }
    return { backgroundColor: "#F3F4F6", textColor: "#374151" }; // Not Started
  };

  const getStatusText = (progress?: number) => {
    if (progress !== undefined) {
      if (progress === 0) {
        return "Not Started";
      } else if (progress > 0 && progress < 100) {
        return "In Progress";
      } else if (progress >= 100) {
        return "Completed";
      }
    }
    return "Not Started";
  };

  const academicStats = [
    {
      icon: <CheckCircleIcon size={20} color="#3E68FE" />,
      value: student?.completedCourses?.toString() || "0",
      label: "completed_courses",
    },
    {
      icon: <ClockIcon size={20} color="#3E68FE" />,
      value: student?.coursesInProgress?.toString() || "0",
      label: "in_progress",
    },
  ];

  const courses = coursesData?.getEnrolledStudentCoursesByAdmin?.data?.courses || [];
  const totalCount = coursesData?.getEnrolledStudentCoursesByAdmin?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleCourseClick = (courseId: string) => {
    const selectedCourseWithMetadata = courses.find((c: any) => c.course.id === courseId);
    const courseName = selectedCourseWithMetadata?.course?.name || "Unknown Course";

    navigation.navigate("Course Information", {
      courseId: courseId,
      courseName,
      returnTo: "Manage Courses",
    });
  };

  const handleViewCourse = (courseId: string) => {
    handleCourseClick(courseId);
  };

  const renderCoursesTable = () => {
    return (
      <View style={styles.tableContainer}>
        <View style={styles.tableHeader}>
          <View style={[styles.cell, styles.courseNameCell]}>
            <CustomText text="course_name" size="s" weight="bold" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.statusCell]}>
            <CustomText text="status" size="s" weight="bold" style={styles.headerText} />
          </View>
          <View style={[styles.cell, styles.actionsCell]}>
            <CustomText text="actions" size="s" weight="bold" style={styles.headerText} />
          </View>
        </View>

        {courses.map((courseWithMetadata: any) => {
          const course = courseWithMetadata.course;
          const progress = courseWithMetadata.progress;
          const statusColors = getStatusColor(progress);

          return (
            <View key={course.id} style={styles.tableRow}>
              <View style={[styles.cell, styles.courseNameCell]}>
                <TouchableOpacity style={styles.courseNameContainer} onPress={() => handleCourseClick(course.id)}>
                  <CustomText text={course.name} size="s" style={styles.courseName} useTranslationText={false} />
                  <ArrowSquareOutIcon size={16} color="#3E68FE" />
                </TouchableOpacity>
              </View>
              <View style={[styles.cell, styles.statusCell]}>
                <Pill
                  text={getStatusText(progress)}
                  backgroundColor={statusColors.backgroundColor}
                  textColor={statusColors.textColor}
                />
              </View>
              <View style={[styles.cell, styles.actionsCell]}>
                <CustomButton
                  text="View Course"
                  leftIcon={<EyeIcon size={14} color="#3E68FE" />}
                  onPress={() => handleViewCourse(course.id)}
                  size="s"
                  styleType="lightStroke"
                  textStyle={{ color: "#3E68FE" }}
                />
              </View>
            </View>
          );
        })}
      </View>
    );
  };

  return (
    <View style={styles.contentSection}>
      {/* Academic Summary Cards */}
      <View style={styles.academicStats}>
        {academicStats.map((stat, index) => (
          <View key={index} style={styles.statCard}>
            <View style={styles.statIcon}>{stat.icon}</View>
            <View style={styles.statContent}>
              <CustomText
                text={stat.value}
                size="xxl"
                weight="ultraBold"
                style={styles.statValue}
                useTranslationText={false}
              />
              <CustomText text={stat.label} size="s" style={styles.statLabel} />
            </View>
          </View>
        ))}
      </View>

      {/* Recent Courses Section */}
      <View style={styles.sectionCard}>
        <View style={styles.sectionHeader}>
          <CustomText text="recent_courses" size="l" weight="bold" />
        </View>

        <AdminTable>
          <AdminTableHeader
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortDropdownOpen={sortDropdownOpen}
            setSortDropdownOpen={setSortDropdownOpen}
            sortItems={SORT_ITEMS}
          />

          {renderCoursesTable()}

          {totalCount > 0 && (
            <AdminTablePagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={totalCount}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              onItemsPerPageChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(1);
              }}
            />
          )}
        </AdminTable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  contentSection: {
    flex: 1,
  },
  academicStats: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 20,
    marginBottom: 32,
    padding: 24,
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  statCard: {
    flex: 1,
    minWidth: 200,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 10,
    backgroundColor: "#F0F4FF",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    color: "#111827",
    marginBottom: 4,
  },
  statLabel: {
    color: "#6B7280",
    textTransform: "uppercase",
  },
  sectionCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
  },
  sectionHeader: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    overflow: "visible",
    zIndex: 10,
  },
  tableContainer: {
    backgroundColor: "#FFFFFF",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  courseNameCell: {
    flex: 4,
  },
  statusCell: {
    flex: 2,
    alignItems: "flex-start",
  },
  actionsCell: {
    flex: 2,
    alignItems: "flex-start",
  },
  headerText: {
    color: "#475569",
  },
  courseNameContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  courseName: {
    color: "#3E68FE",
  },
});

export default AcademicOverviewTab;
